import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Popper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  useGridApiRef,
  DataGridPro,
  GridActionsCellItem,
  GridCellEditStartReasons,
  GridCellEditStopReasons,
  GridRowEditStopReasons,
  GridRowModes,
} from '@mui/x-data-grid-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  deleteIssueContractStage,
  getIssueContractStage,
  getIssueContractStages,
  getIssueCreateContractStages,
  saveEditIssueContractStage,
  saveIssueContractStage,
} from '../../../../../packages/common/api';
import ChevronIcon from '../../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import DeleteIconBig from '../../../../../packages/common/shared-ui/src/icons/DeleteIconBig';
import PlusIcon from '../../../../../packages/common/shared-ui/src/icons/PlusIcon';
import SortIcon from '../../../../../packages/common/shared-ui/src/icons/SortIcon';
import SubmitIcon from '../../../../../packages/common/shared-ui/src/icons/SubmitIcon';
import UserNewEditIcon from '../../../../../packages/common/shared-ui/src/icons/UserNewEditIcon';
import { useTranslation } from '../../../../../packages/common/utils/getLocalString';
import { getTranslatedText } from '../../../../../packages/common/utils/getTranslatedText';
import { LanguageMap } from '../../../../../packages/common/utils/languageMap';

// Original class: selectLabel
const StyledSelectLabel = styled(InputLabel)(theme => ({
  '&.MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '14px',
    lineHeight: '20px',
    marginTop: '-6px',
    transform: 'none',
    left: '8px',
    top: '15px',
    '&.Mui-focused': {
      transform: 'none',
      left: '10px',
      top: '15px',
    },
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },
}));
// Original class: rowSelect
const StyledRowSelect = styled(Select)(theme => ({
  '&.MuiInputBase-root.MuiInput-root': {
    marginTop: 0,
    width: '100%',
    fontSize: '14px',
    lineHeight: '20px',
    height: '100%',
    padding: '8px',
    '&::before': {
      borderBottom: 'none',
    },
    '&:hover': {
      '&::before': {
        borderBottom: 'none',
      },
    },
    '& .MuiSelect-select': {
      padding: 0,
      '&:focus': {
        backgroundColor: 'transparent',
      },
    },
    '& .MuiSvgIcon-root.MuiSelect-icon': {
      width: '20px',
      height: '20px',
      position: 'static',
    },
    '& .MuiSelect-nativeInput': {
      padding: '0',
    },
    '&.Mui-error': {
      border: '1px solid #E03737',
      '&::after': {
        display: 'none',
      },
    },
  },
}));
// Original class: paperDropdown
const StyledPaperDropdown = styled(Paper)(theme => ({
  '&.MuiPaper-root.MuiPopover-paper.MuiMenu-paper': {
    position: 'absolute',
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: '300px',
    borderRadius: '0 0 16px 16px',
    borderTop: '1px solid #E4E4EF',
    boxShadow: '0px 5px 10px -8px #A0A1AA66',
    '&::-webkit-scrollbar': {
      width: '2px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#7174AC',
      borderRadius: '5px',
    },
    '& .MuiList-root.MuiMenu-list': {
      padding: '0',
      '& li': {
        fontSize: '14px',
        lineHeight: '20px',
        color: '#212346',
        '&.MuiButtonBase-root.MuiMenuItem-root': {
          '&:hover': {
            backgroundColor: '#DCDFF4',
          },
        },
      },
    },
  },
}));
// Original class: datePicker
const StyledDatePicker = styled(DatePicker)(theme => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    height: '100%',
    '& .MuiInputBase-input.MuiOutlinedInput-input': {
      padding: 0,
      color: '#7174AC',
      fontSize: '14px',
    },
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    },
  },

  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));
// Original class: rowInput
const StyledRowInput = styled(TextField)(theme => ({
  '& .MuiInputBase-root.MuiInput-root': {
    fontSize: '14px',
    lineHeight: '20px',
    height: '100%',
    '&::before': {
      borderBottom: 'none',
    },
    '&:hover': {
      '&::before': {
        borderBottom: 'none',
      },
    },
    '& .MuiInputBase-input.MuiInput-input': {
      color: '#7174AC',
      padding: 0,
      '&::placeholder': {
        opacity: 1,
      },
    },
    '&.Mui-error': {
      border: '1px solid #E03737',
      '&::after': {
        display: 'none',
      },
    },
  },
}));
// Original class: SpentTimeTableRoot
const StyledSpentTimeTableRoot = styled(DataGridPro)(({ theme }) => ({
  '&.MuiDataGrid-root': {
    borderRadius: '16px 16px 0 0',
    borderColor: '#E4E4EF',
    backgroundColor: '#ffffff',
    '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor':
      {
        '& + .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
          borderLeft: '1px solid #E4E4EF',
          '&.actions': {
            borderLeft: 'none',
          },
        },
        '& .MuiDataGrid-columnHeaderTitleContainer': {
          whiteSpace: 'initial',
        },
      },
    '& .MuiDataGrid-row.Mui-selected': {
      backgroundColor: '#DCDFF4',
    },
    '& .MuiDataGrid-row.Mui-selected.Mui-hovered': {
      backgroundColor: '#DCDFF4',
    },
    '& .MuiDataGrid-main .MuiDataGrid-iconButtonContainer .MuiButtonBase-root':
      {
        marginLeft: theme.spacing(2),
      },
    '& .MuiDataGrid-columnSeparator': {
      '& .MuiSvgIcon-root': {
        width: '24px',
      },
    },
    '& .MuiDataGrid-cell': {
      '&.tableCell': {
        //lineHeight: '20px',
        '& .MuiCheckbox-root': {
          padding: 0,
        },
        '&.textCenter': {
          justifyContent: 'center',
        },
        '&.select.MuiDataGrid-cell--editing': {
          padding: 0,
        },
        '& + .tableCell': {
          borderLeft: '1px solid #E4E4EF',
        },
        '&.MuiDataGrid-cell--editing': {
          padding: '0 8px',
        },
      },
      '&.actions': {
        padding: 0,
        '& .MuiDataGrid-actionsCell': {
          height: '100%',
          width: '100%',
          gridGap: 0,
        },
        '& .MuiButtonBase-root.MuiIconButton-root': {
          height: '100%',
          borderRadius: '0',
          padding: '0',
          flex: 1,
          '& .MuiSvgIcon-root': {
            width: '16px',
            height: '16px',
          },
          '& + .MuiButtonBase-root.MuiIconButton-root': {
            borderLeft: '1px solid #E4E4EF',
          },
        },
      },
      '& a': {
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
  },
}));
// Original class: tooltipForm
const StyledTooltipForm = styled(Popper)(theme => ({
  '& .MuiTooltip-tooltip': {
    backgroundColor: '#F1F2F8',
    borderRadius: '8px',
    padding: '4px',
    color: '#212346',
    fontSize: '12px',
    lineHeight: '20px',
    fontWeight: 400,
    margin: 0,
    marginBottom: '0!important',
  },
}));
const IssueContractStages = () => {
  const getLocalString = useTranslation();
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [contractStagesColumns, setContractStagesColumns] = useState([]);
  const [contractStagesRows, setContractStagesRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [error, setError] = useState(false);
  const [language, setLanguage] = useState('ru');
  const inputRefs = useRef({});
  const apiRef = useGridApiRef();

  const formattedContractStagesRows = useCallback(
    data =>
      data.reduce((acc, row) => {
        const newCols = row.columns.reduce((columnAcc, _, i) => {
          return {
            ...columnAcc,
            [row.columns[i].fieldId]: row.columns[i].value,
          };
        }, {});
        return [...acc, { ...newCols, id: row.rowId }];
      }, []),
    [],
  );

  useEffect(() => {
    window.localStorage.getItem('lang') &&
      setLanguage(LanguageMap.get(window.localStorage.getItem('lang')).lang);
  }, []);
  useEffect(() => {
    let ignore = false;
    async function getContractStages() {
      const { data: contractStages } = await getIssueContractStages(params.id);
      if (!ignore) {
        return contractStages;
      }
    }
    getContractStages()
      .then(res => {
        const contractStagesRowsValue = formattedContractStagesRows(
          res.rows.filter(item => item.rowId !== 'total'),
        );
        setContractStagesRows(contractStagesRowsValue);
        setContractStagesColumns(res.fields);
        setLoading(false);
      })
      .catch(error => console.error(error, 'ERROR IN GET'));
    return () => {
      ignore = true;
    };
  }, [params.id]);
  const handleTextField = useCallback(
    (event, id) => {
      const input = event.target;
      const cursorPosition = input.selectionStart;

      const index = contractStagesRows.findIndex(item => item.id === id);
      const updatedObject = {
        ...contractStagesRows[index],
        comment: event.target.value,
      };

      setContractStagesRows([
        ...contractStagesRows.slice(0, index),
        updatedObject,
        ...contractStagesRows.slice(index + 1),
      ]);
      setTimeout(() => {
        const currentInput = inputRefs.current[id];
        if (currentInput) {
          currentInput.setSelectionRange(cursorPosition, cursorPosition);
        }
      }, 0);
    },
    [contractStagesRows],
  );
  const handleSelect = useCallback(
    (event, id) => {
      const index = contractStagesRows.findIndex(item => item.id === id);
      const updatedObject = {
        ...contractStagesRows[index],
        stageId: event.target.value,
      };
      setContractStagesRows([
        ...contractStagesRows.slice(0, index),
        updatedObject,
        ...contractStagesRows.slice(index + 1),
      ]);
    },
    [contractStagesRows],
  );
  const dateHandler = useCallback(
    (value, id) => {
      const index = contractStagesRows.findIndex(item => item.id === id);
      const newDate = value && value.isValid() ? value : null;

      const updatedObject = {
        ...contractStagesRows[index],
        startDate: newDate,
        ['contract_stage.start_date']: newDate,
      };

      setContractStagesRows([
        ...contractStagesRows.slice(0, index),
        updatedObject,
        ...contractStagesRows.slice(index + 1),
      ]);
    },
    [contractStagesRows],
  );

  const handleEditClick = useCallback(
    async id => {
      const index = contractStagesRows.findIndex(item => item.id === id);
      try {
        const { data: response } = await getIssueContractStage(id);
        if (response) {
          const updatedObject = {
            ...response,
            id: id,
            ['contract_stage.customer']: response.customerName,
            ['contract_stage.author']: response.authorName,
            ['contract_stage.stage']: response.stageName,
            ['contract_stage.start_date']: response.startDate,
            ['contract_stage.comment']: response.comment,
          };
          setContractStagesRows([
            ...contractStagesRows.slice(0, index),
            updatedObject,
            ...contractStagesRows.slice(index + 1),
          ]);
          setRowModesModel(oldModel => ({
            ...oldModel,
            [id]: {
              mode: GridRowModes.Edit,
            },
          }));
        }
      } catch (error) {
        console.error('ERROR WITH GET', error);
      }
    },
    [contractStagesRows],
  );
  const validateRow = useCallback(
    index => {
      if (!contractStagesRows[index].stageId) {
        const updatedObject = {
          ...contractStagesRows[index],
          error: true,
        };
        setContractStagesRows([
          ...contractStagesRows.slice(0, index),
          updatedObject,
          ...contractStagesRows.slice(index + 1),
        ]);
        setError(true);
        return false;
      } else {
        setError(false);
        return true;
      }
    },
    [contractStagesRows],
  );
  const handleSaveClick = useCallback(
    async (id, row) => {
      const validation = validateRow(
        contractStagesRows.findIndex(item => item.id === id),
      );
      if (!validation) {
        return;
      }

      try {
        apiRef.current.stopRowEditMode({ id: id });
      } catch (error) {
        console.error('ERROR WITH SAVE', error);
      }
    },
    [apiRef, contractStagesRows, validateRow],
  );
  const processRowUpdate = useCallback(async (newRow, oldRow) => {
    try {
      let response;
      const startDateValue = newRow['contract_stage.start_date'];
      const dateForServer = startDateValue ? dayjs(startDateValue) : dayjs();
      if (!newRow.new) {
        const data = {
          stageId: Number(newRow.stageId),
          startDate: dateForServer.format('YYYY-MM-DD'),
          comment: newRow.comment,
        };
        response = await saveEditIssueContractStage(newRow.id, data);
      }

      if (newRow.new) {
        const data = {
          issueId: newRow.issueId,
          customerId: newRow.customerId,
          authorId: newRow.authorId,
          stageId: Number(newRow.stageId),
          startDate: dateForServer.format('YYYY-MM-DD'),
          comment: newRow.comment,
        };
        response = await saveIssueContractStage(data);
      }

      if (response) {
        const { data: res } = response;
        const updatedObject = {
          ...res,
          id: res.id,
          ['contract_stage.customer']: res.customerName,
          ['contract_stage.author']: res.authorName,
          ['contract_stage.stage']: res.stageName,
          ['contract_stage.start_date']: dayjs(res.startDate).format(
            'DD.MM.YYYY',
          ),
          ['contract_stage.comment']: res.comment,
        };
        setContractStagesRows(prevRows =>
          prevRows.map(row => (row.id === newRow.id ? updatedObject : row)),
        );
        setRowModesModel(oldModel => ({
          ...oldModel,
          [newRow.id]: { mode: GridRowModes.View, ignoreModifications: true },
        }));

        return updatedObject;
      }
    } catch (error) {
      console.error('ERROR WITH GET IN processRowUpdate', error);
      return oldRow;
    }

    return newRow;
  }, []);
  const handleDelete = useCallback(
    async (id, row) => {
      if (row.new) {
        const newArray = contractStagesRows.filter(item => item.id !== row.id);
        setContractStagesRows(newArray);
        return;
      }
      try {
        const response = await deleteIssueContractStage(id);
        if (response) {
          setContractStagesRows(
            contractStagesRows.filter(row => row.id !== id),
          );
        }
      } catch (error) {
        console.error('ERROR WITH DELETE', error);
      }
    },
    [contractStagesRows],
  );

  const formattedContractStagesColumns = useCallback(() => {
    const smallFields = ['date', 'bool'];
    const editableColumns = ['stage', 'start_date', 'comment'];
    const columns = contractStagesColumns.map(column => {
      return {
        field: column.fieldId,
        headerName: column.fieldName,
        renderHeader: props => (
          <Typography
            variant='h4'
            sx={{
              fontWeight: 700,
            }}
          >
            {props.colDef.headerName}
          </Typography>
        ),
        disableExport: false,
        position: column.position,
        flex: smallFields.includes(column.fieldType) ? 0 : 1,
        width: smallFields.includes(column.fieldType) && 150,
        cellClassName: props =>
          props.field === 'users.admin'
            ? 'tableCell textCenter'
            : props.field === 'contract_stage.stage'
              ? 'tableCell select'
              : 'tableCell',
        editable: editableColumns.includes(column.field) && true,
        renderCell: props => {
          if (props.field === 'contract_stage.comment') {
            return (
              <Box id={props.id + props.field}>
                <Box
                  sx={{
                    visibility: 'hidden',
                    height: 0,
                  }}
                >
                  {props.value}
                </Box>
                <Tooltip
                  placement='top-start'
                  disableHoverListener={!props.value}
                  title={props.value}
                  slots={{
                    popper: StyledTooltipForm,
                  }}
                >
                  <Box
                    sx={{
                      width: `${props.colDef.computedWidth - 20}px`,
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                    }}
                  >
                    {props.value}
                  </Box>
                </Tooltip>
              </Box>
            );
          }
        },
        renderEditCell: props => {
          const index = contractStagesRows.findIndex(
            item => item.id === props.row.id,
          );
          if (props.field === 'contract_stage.stage') {
            return (
              <FormControl sx={{ width: '100%' }} error={props.row.error}>
                <StyledSelectLabel id='select-label'>
                  {getLocalString('labels', 'select')}
                </StyledSelectLabel>
                <StyledRowSelect
                  native={false}
                  labelId='select-label'
                  label={getLocalString('labels', 'select')}
                  variant='standard'
                  sx={{ width: '100%', border: 'none' }}
                  MenuProps={{
                    slots: { paper: StyledPaperDropdown },
                  }}
                  IconComponent={props => (
                    <ChevronIcon direction={'down'} {...props} />
                  )}
                  value={props.row.stageId ? props.row.stageId : ''}
                  renderValue={value => {
                    return props?.row?.stages?.find(
                      stage => stage.valueId === value.toString(),
                    )
                      ? props?.row?.stages?.find(
                          stage => stage.valueId === value.toString(),
                        ).valueName
                      : '';
                  }}
                  onChange={event => handleSelect(event, props.row.id)}
                >
                  {props?.row?.stages?.map((item, index) => (
                    <MenuItem key={index} value={item.valueId}>
                      {item.valueName}
                    </MenuItem>
                  ))}
                </StyledRowSelect>
              </FormControl>
            );
          }
          if (props.field === 'contract_stage.start_date') {
            return (
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={language}
              >
                <StyledDatePicker
                  slotProps={{
                    textField: {
                      placeholder: getLocalString('labels', 'input'),
                      onFocus: event => {
                        event.stopPropagation();
                      },
                      onKeyDown: event => {
                        event.stopPropagation();
                      },
                    },
                  }}
                  format={'DD.MM.YYYY'}
                  value={props.value ? dayjs(props.value) : null}
                  onChange={newValue => {
                    if (newValue && newValue.isValid()) {
                      props.api.setEditCellValue({
                        id: props.id,
                        field: props.field,
                        value: newValue.toISOString(),
                      });
                    }
                  }}
                  closeOnSelect={true}
                  slots={{
                    leftArrowIcon: () => (
                      <ChevronIcon
                        direction='left'
                        viewBox='0 0 24 24'
                        width={24}
                        height={24}
                      />
                    ),
                    rightArrowIcon: () => (
                      <ChevronIcon
                        direction='right'
                        viewBox='0 0 24 24'
                        width={24}
                        height={24}
                      />
                    ),
                    rightArrowButton: componentProps => (
                      <IconButton disableRipple {...componentProps}>
                        {componentProps.children}
                      </IconButton>
                    ),
                    leftArrowButton: componentProps => (
                      <IconButton disableRipple {...componentProps}>
                        {componentProps.children}
                      </IconButton>
                    ),
                  }}
                />
              </LocalizationProvider>
            );
          }
          if (props.field === 'contract_stage.comment') {
            return (
              <StyledRowInput
                inputRef={el => el?.focus()}
                placeholder={getLocalString('labels', 'input')}
                defaultValue={props.row.comment || ''}
                variant='standard'
                onBlur={event => {
                  if (event.target.value !== (props.row.comment || '')) {
                    handleTextField(event, props.row.id);
                  }
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.target.blur();
                  }
                }}
                fullWidth
              />
            );
          }
        },
      };
    });
    return [
      ...columns,
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        headerClassName: 'actions',
        width: 70,
        cellClassName: 'actions tableCell',
        getActions: ({ id, row }) => {
          const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
          if (isInEditMode) {
            return [
              <GridActionsCellItem
                onClick={() => handleSaveClick(id, row)}
                icon={<SubmitIcon />}
                label='Save'
              />,
              <GridActionsCellItem
                onClick={() => handleDelete(id, row)}
                icon={<DeleteIconBig />}
                label='Cancel'
              />,
            ];
          }
          return [
            <GridActionsCellItem
              onClick={() => handleEditClick(id)}
              icon={
                <UserNewEditIcon width='16' height='16' viewBox='0 0 16 16' />
              }
              label='Edit'
            />,
            <GridActionsCellItem
              onClick={() => handleDelete(id, row)}
              icon={<DeleteIconBig />}
              label='Delete'
              color='inherit'
            />,
          ];
        },
      },
    ];
  }, [
    language,
    contractStagesColumns,
    contractStagesRows,
    getLocalString,
    handleDelete,
    handleEditClick,
    handleSaveClick,
    handleSelect,
    handleTextField,
    rowModesModel,
  ]);

  const contractStagesColumnsValue = formattedContractStagesColumns();

  const addRowHandler = async () => {
    try {
      const { data: response } = await getIssueCreateContractStages(params.id);
      if (response) {
        setContractStagesRows(oldRows => [
          ...oldRows,
          {
            new: true,
            id: `${response.issueId}-row${contractStagesRows.length + 1}`,
            ...response,
            ['contract_stage.customer']: response.customerName,
            ['contract_stage.author']: response.authorName,
            ['contract_stage.start_date']: dayjs(new Date()),
          },
        ]);
        setRowModesModel(oldModel => ({
          ...oldModel,
          [`${response.issueId}-row${contractStagesRows.length + 1}`]: {
            mode: GridRowModes.Edit,
            fieldToFocus: 'contract_stage.customer',
          },
        }));
      }
    } catch (error) {
      console.error('ERROR WITH GET', error);
    }
  };

  const handleRowModesModelChange = useCallback(newRowModesModel => {
    setRowModesModel(newRowModesModel);
  }, []);

  const handleRowEditStop = useCallback((params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  }, []);

  const processRowUpdatetemp = useCallback((newRow, oldRow) => {
    console.log('processRowUpdate');
    setContractStagesRows(prevRows =>
      prevRows.map(row => (row.id === newRow.id ? newRow : row)),
    );
    return newRow;
  }, []);

  return (
    <>
      {loading ? (
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '70px',
          }}
        >
          <CircularProgress color='secondary' />
        </Grid>
      ) : (
        <>
          <Typography
            variant='h3'
            sx={{
              color: '#082253',
              fontWeight: 600,
            }}
          >
            {getLocalString('IssuePage', 'sections', 'contractStages', 'title')}
          </Typography>
          <Grid
            container
            direction={'column'}
            sx={{
              marginTop: '18px',
              maxWidth: '100%',
              position: 'relative',
            }}
          >
            {error && (
              <Box sx={{ position: 'absolute', top: '-20px', left: 0 }}>
                <Typography
                  variant='h3'
                  sx={{
                    color: '#E03737',
                  }}
                >
                  {getLocalString('errors', 'emptyFields')}
                </Typography>
              </Box>
            )}
            <Grid
              sx={{
                width: '100%',
              }}
            >
              <StyledSpentTimeTableRoot
                apiRef={apiRef}
                localeText={{
                  noRowsLabel: getLocalString('labels', 'noData'),
                }}
                experimentalFeatures={{ newEditingApi: true }}
                autoHeight
                hideFooter
                disableRowSelectionOnClick
                disableColumnMenu
                columns={contractStagesColumnsValue}
                rows={contractStagesRows}
                getRowClassName={params => !params.row.active && 'disabled'}
                pageSize={10}
                slots={{
                  columnUnsortedIcon: () => (
                    <SortIcon width='20' height='20' viewBox='0 0 20 20' />
                  ),
                  columnSortedAscendingIcon: () => (
                    <SortIcon
                      direction='up'
                      width='20'
                      height='20'
                      viewBox='0 0 20 20'
                    />
                  ),
                  columnSortedDescendingIcon: () => (
                    <SortIcon
                      direction='down'
                      width='20'
                      height='20'
                      viewBox='0 0 20 20'
                    />
                  ),
                }}
                rowHeight={40}
                editMode='row'
                rowModesModel={rowModesModel}
                onRowModesModelChange={handleRowModesModelChange}
                onRowEditStop={handleRowEditStop}
                processRowUpdate={processRowUpdate}
                onRowEditStart={(params, event) => {
                  if (
                    params.reason === GridCellEditStartReasons.printableKeyDown
                  ) {
                    event.defaultMuiPrevented = true;
                  }
                  if (
                    params.reason === GridCellEditStartReasons.cellDoubleClick
                  ) {
                    event.defaultMuiPrevented = true;
                  }
                }}
              />
            </Grid>
            <Grid
              sx={{
                padding: '16px',
                borderRadius: '0 0 16px 16px',
                border: '1px solid #E4E4EF',
                borderTop: 'none',
              }}
            >
              <IconButton onClick={addRowHandler} sx={{ padding: '0' }}>
                <PlusIcon width='24' height='24' viewBox='0 0 24 24' />
              </IconButton>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};
export default IssueContractStages;
